/**
 * FlowLegend Component
 *
 * Legend explaining color coding and node types in the flow diagram.
 */

import { clsx } from 'clsx';
import type { LLMProvider, ContributionType } from '../../../lib/api';

interface FlowLegendProps {
  showModels?: boolean;
  showContributionTypes?: boolean;
  compact?: boolean;
}

const MODEL_CONFIG: Record<LLMProvider, { name: string; icon: string; color: string; bg: string }> = {
  CLAUDE_OPUS_4_5: {
    name: 'Dr. Precision',
    icon: '🩺',
    color: 'text-purple-700',
    bg: 'bg-purple-100',
  },
  CLAUDE_OPUS_4_6: {
    name: 'Dr. Precision 4.6',
    icon: '🩺',
    color: 'text-violet-700',
    bg: 'bg-violet-100',
  },
  GPT_5_2_PRO: {
    name: 'Dr. Evidence',
    icon: '📊',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
  },
  GEMINI_3_PRO: {
    name: 'Dr. Advocate',
    icon: '🤝',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
  },
};

const CONTRIBUTION_TYPE_CONFIG: Record<ContributionType, { label: string; icon: string }> = {
  PROPOSAL: { label: 'Initial Proposal', icon: '📝' },
  CRITIQUE: { label: 'Peer Review', icon: '🔍' },
  DEFENSE: { label: 'Defense', icon: '🛡️' },
  CONCESSION: { label: 'Concession', icon: '🤝' },
  CLARIFICATION: { label: 'Clarification', icon: '💡' },
  SYNTHESIS: { label: 'Synthesis', icon: '⚖️' },
};

export function FlowLegend({
  showModels = true,
  showContributionTypes = true,
  compact = false,
}: FlowLegendProps) {
  return (
    <div
      className={clsx(
        'rounded-lg border border-gray-200 bg-white p-3',
        compact ? 'text-xs' : 'text-sm'
      )}
    >
      <h4 className="font-semibold text-gray-700 mb-2">Legend</h4>

      <div className={clsx('space-y-3', compact && 'space-y-2')}>
        {/* Expert Models */}
        {showModels && (
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Experts</span>
            <div className={clsx('mt-1 flex flex-wrap gap-2', compact && 'gap-1')}>
              {Object.entries(MODEL_CONFIG).map(([model, config]) => (
                <span
                  key={model}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded',
                    config.bg,
                    config.color
                  )}
                >
                  <span>{config.icon}</span>
                  {!compact && <span>{config.name}</span>}
                </span>
              ))}
              {/* Moderator */}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                <span>👨‍⚕️</span>
                {!compact && <span>Chief of Medicine</span>}
              </span>
            </div>
          </div>
        )}

        {/* Contribution Types */}
        {showContributionTypes && (
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Contribution Types</span>
            <div className={clsx('mt-1 flex flex-wrap gap-2', compact && 'gap-1')}>
              {Object.entries(CONTRIBUTION_TYPE_CONFIG).map(([type, config]) => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700"
                >
                  <span>{config.icon}</span>
                  {!compact && <span>{config.label}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Flow Direction */}
        <div>
          <span className="text-gray-500 text-xs uppercase tracking-wide">Flow</span>
          <div className="mt-1 flex items-center gap-4">
            <span className="inline-flex items-center gap-1 text-gray-600">
              <span>📥</span>
              {!compact && <span>Input Data</span>}
            </span>
            <span className="text-gray-400">→</span>
            <span className="inline-flex items-center gap-1 text-gray-600">
              <span>🔄</span>
              {!compact && <span>Rounds</span>}
            </span>
            <span className="text-gray-400">→</span>
            <span className="inline-flex items-center gap-1 text-gray-600">
              <span>✅</span>
              {!compact && <span>Consensus</span>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline legend for quick reference in headers
 */
export function InlineLegend() {
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-purple-500" />
        <span className="text-gray-500">Claude</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-emerald-500" />
        <span className="text-gray-500">GPT</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-blue-500" />
        <span className="text-gray-500">Gemini</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-indigo-500" />
        <span className="text-gray-500">Moderator</span>
      </span>
    </div>
  );
}
